import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, FlaskConical, Sparkles, Leaf, Home as HomeIcon, Salad, Dna, Download, Newspaper, Clock } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import { Button } from "@/components/ui/button";

const LINE_ICONS = {
  "cuidado-personal": Leaf,
  "hogar-limpio": HomeIcon,
  "nutricion-limpia": Salad,
  "longevidad-celular": Dna,
};

const LINE_IMAGES = {
  "cuidado-personal": "https://images.unsplash.com/photo-1611930021559-4a5cb5c38da3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "hogar-limpio": "https://images.unsplash.com/photo-1650964336599-a6b32f916378?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "nutricion-limpia": "https://images.unsplash.com/photo-1590779033100-9f60a05a013d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "longevidad-celular": "https://images.unsplash.com/photo-1579165466741-7f35e4755660?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
};

const Home = () => {
  const [lines, setLines] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [articles, setArticles] = useState([]);
  const [leadOpen, setLeadOpen] = useState(false);
  const [sectionEmail, setSectionEmail] = useState("");

  useEffect(() => {
    api.get("/taxonomy").then(({ data }) => setLines(data.lines)).catch(() => {});
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data)).catch(() => {});
    api.get("/articles").then(({ data }) => setArticles(data.slice(0, 3))).catch(() => {});
    const t = setTimeout(() => setLeadOpen(true), 12000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden aura">
        <div className="absolute inset-0 bio-grid opacity-[0.35]" />
        <div className="relative z-10 px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-24 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 font-mono-tech text-[11px] uppercase tracking-widest text-emerald-400">
              <FlaskConical className="h-3.5 w-3.5" /> Curaduría científica no tóxica
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Vive más y mejor con productos <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-300 bg-clip-text text-transparent">verdaderamente limpios</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Analizamos ingredientes, certificaciones y estudios para curar solo lo que supera nuestro <strong className="text-foreground">Sello ZeroToxic</strong>. Longevidad, higiene, hogar y nutrición sin toxinas.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/catalogo">
                <Button data-testid="hero-catalog-btn" className="h-12 px-6 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-950 font-bold rounded-full text-base">
                  Explorar Catálogo Curado <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Button data-testid="hero-guide-btn" onClick={() => setLeadOpen(true)} variant="outline" className="h-12 px-6 rounded-full border-emerald-500/30 hover:bg-emerald-500/10 text-base">
                <Download className="mr-1 h-4 w-4" /> Guía de 30 Días
              </Button>
            </div>
            <div className="mt-12 flex flex-wrap gap-8">
              {[["1,200+", "Productos analizados"], ["380+", "Tóxicos evitados"], ["19", "Filtros de pureza"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-3xl font-extrabold text-emerald-400 text-glow">{n}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* LINES */}
      <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-16 lg:py-24">
        <div className="mb-10">
          <span className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400">Nuestras 4 líneas</span>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Explora por categoría curada</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {lines.map((line, i) => {
            const Icon = LINE_ICONS[line.id] || Leaf;
            return (
              <motion.div key={line.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
                <Link to={`/categoria/${line.id}`} data-testid={`line-card-${line.id}`} className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card h-full hover:border-emerald-500/50 hover:shadow-[0_8px_30px_rgba(16,185,129,0.12)] transition-all duration-300 hover:-translate-y-1">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={LINE_IMAGES[line.id]} alt={line.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                    <span className="absolute top-3 left-3 flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/90 text-slate-950">
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold leading-snug group-hover:text-emerald-400 transition-colors">{line.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{line.tagline}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-mono-tech text-emerald-400">Ver línea <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* TRUST SEAL */}
      <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl border border-emerald-500/20 bg-secondary/40 p-8 lg:p-14 aura">
          <div className="relative z-10 grid md:grid-cols-[auto_1fr] gap-8 items-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-slate-950">
              <ShieldCheck className="h-10 w-10" />
            </div>
            <div>
              <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">¿Qué significa el Sello ZeroToxic?</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed max-w-3xl">
                Cada producto pasa un filtro de curaduría: analizamos la lista completa de ingredientes, descartamos disruptores endocrinos y aceites de semillas,
                verificamos certificaciones independientes (EWG, B-Corp, análisis de terceros) y priorizamos empaques sin plástico. Solo lo que aprueba llega a tu catálogo.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                {["0% Fraguas químicas", "100% Ingredientes limpios", "Análisis de terceros"].map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-1.5 text-xs font-medium text-emerald-400">
                    <Sparkles className="h-3.5 w-3.5" /> {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-16 lg:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400">Selección del curador</span>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Productos destacados</h2>
          </div>
          <Link to="/catalogo" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-emerald-400 hover:gap-2 transition-all">
            Ver todo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* NOVEDADES / ÚLTIMAS GUÍAS */}
      {articles.length > 0 && (
        <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto pb-4">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-emerald-400">
                <Newspaper className="h-3.5 w-3.5" /> Novedades
              </span>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Últimas guías publicadas</h2>
            </div>
            <Link to="/blog" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-emerald-400 hover:gap-2 transition-all">
              Ver todas <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`home-article-${a.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card hover:border-emerald-500/50 hover:shadow-[0_8px_30px_rgba(16,185,129,0.12)] transition-all duration-300 hover:-translate-y-1"
              >
                <Link to={`/blog/${a.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-secondary">
                  <img src={a.cover_image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </Link>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <Clock className="h-3 w-3" /> {a.read_minutes} min · {a.author}
                  </div>
                  <Link to={`/blog/${a.slug}`}>
                    <h3 className="font-display font-semibold leading-snug group-hover:text-emerald-400 transition-colors line-clamp-2">{a.title}</h3>
                  </Link>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {/* LEAD CAPTURE SECTION */}
      <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto pb-16 lg:pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 to-cyan-600 p-8 lg:p-14 text-slate-950">
          <div className="relative z-10 max-w-2xl">
            <span className="font-mono-tech text-xs uppercase tracking-widest">Recurso gratuito</span>
            <h2 className="mt-2 font-display text-2xl lg:text-4xl font-extrabold tracking-tight">Guía de 30 Días para Desintoxicar tu Cuerpo y Hogar</h2>
            <p className="mt-3 text-slate-900/80 leading-relaxed">Un protocolo semana a semana para reemplazar productos tóxicos por alternativas limpias, sin abrumarte. Descárgala gratis.</p>
            <form
              onSubmit={(e) => { e.preventDefault(); setLeadOpen(true); }}
              className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg"
            >
              <input
                data-testid="section-lead-email"
                type="email"
                value={sectionEmail}
                onChange={(e) => setSectionEmail(e.target.value)}
                placeholder="tu@correo.com"
                className="h-12 flex-1 rounded-full bg-white/90 px-5 text-sm text-slate-900 outline-none placeholder:text-slate-500"
              />
              <button data-testid="section-lead-submit" type="submit" className="h-12 rounded-full bg-slate-950 px-6 font-bold text-white hover:bg-slate-800 transition-colors">
                Obtener Guía
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
      <LeadCaptureModal open={leadOpen} onOpenChange={setLeadOpen} />
    </div>
  );
};

export default Home;
